import React, { useEffect, useState } from "react";
import { IconButton, MessageBar, MessageBarType, Persona, PersonaSize, TooltipHost } from "@fluentui/react";
import SearchBox from "@/ui-controls/SearchBox";
import {
  User,
  UserWithCustomers,
  getUsersPaged,
  UsersQueryParams,
  reactivateUser,
} from "@/api/users";
import UsersFilterPanel, { UserStatusFilter, UserSortBy } from "@/components/users/UsersFilterPanel";
import { ServerDataGrid, ServerDataGridColumn } from "@/ui-controls/ServerDataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import DeleteUser from "@/components/users/DeleteUser";
import UserAccessPanel from "@/components/users/UserAccessPanel";

const UsersPage = () => {
  const [users, setUsers] = useState<UserWithCustomers[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState<UserStatusFilter>("all");
  const [appliedStatusFilter, setAppliedStatusFilter] =
    useState<UserStatusFilter>("all");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [selectedSortBy, setSelectedSortBy] = useState<UserSortBy>("Id");
  const [sortDescending, setSortDescending] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [usersToDeactivate, setUsersToDeactivate] = useState<User[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isAccessPanelOpen, setIsAccessPanelOpen] = useState<boolean>(false);
  const [accessUserId, setAccessUserId] = useState<number | null>(null);
  const [accessUserEmail, setAccessUserEmail] = useState<string>("");

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const fetchUsers = (override?: Partial<UsersQueryParams>) => {
    setLoading(true);
    const params: UsersQueryParams = {
      statusFilter: appliedStatusFilter,
      emailSearch: searchTerm,
      pageSize,
      currentPage,
      sortBy: selectedSortBy,
      sortDirection: sortDescending ? 1 : 0,
      ...override,
    };

    getUsersPaged(params)
      .then((result) => {
        setUsers(result.items || []);
        setTotalCount(result.totalCount || 0);
        setTotalPages(result.totalPages || 1);
        setPageSize(result.pageSize || pageSize);
        setCurrentPage(result.currentPage || currentPage);
        setLoading(false);
      })
      .catch((error) => {
        handleError(error);
        setLoading(false);
      });
  };

  const handleError = (error: any) => {
    // Error handling would go here in real app
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setSelectedStatusFilter("all");
    setAppliedStatusFilter("all");
    setCurrentPage(1);
    fetchUsers({
      statusFilter: "all",
      emailSearch: "",
      currentPage: 1,
    });
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const openFilterPanel = () => {
    setIsFilterPanelOpen(true);
  };

  const closeFilterPanel = () => {
    setIsFilterPanelOpen(false);
  };

  const handleStatusFilterChange = (status: UserStatusFilter) => {
    setSelectedStatusFilter(status);
  };

  const handleApplyFilters = () => {
    setAppliedStatusFilter(selectedStatusFilter);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedStatusFilter("all");
    setAppliedStatusFilter("all");
    setCurrentPage(1);
  };

  const handleShowSuccess = (message: string) => {
    setSuccessMessage(message);
    setErrorMessage(null);
  };

  const handleShowError = (message: string) => {
    setErrorMessage(message);
    setSuccessMessage(null);
  };

  useEffect(() => {
    fetchUsers();
  }, [
    appliedStatusFilter,
    searchTerm,
    pageSize,
    currentPage,
    selectedSortBy,
    sortDescending,
  ]);

  const hasStatusFilterApplied = appliedStatusFilter !== "all";
  const totalFiltersApplied = hasStatusFilterApplied ? 1 : 0;
  const selectedUsersOnPage = users.filter((user) =>
    selectedUserIds.includes(user.Id)
  );
  const allSelectedActive =
    selectedUsersOnPage.length > 0 &&
    selectedUsersOnPage.every((user) => user.IsActive);
  const allSelectedInactive =
    selectedUsersOnPage.length > 0 &&
    selectedUsersOnPage.every((user) => !user.IsActive);
  const handleBulkDeactivate = async () => {
    setUsersToDeactivate(selectedUsersOnPage);
  };
  const handleBulkReactivate = async () => {
    await Promise.all(selectedUsersOnPage.map((user) => reactivateUser(user.Id)));
    setSelectedUserIds([]);
    setSelectedCount(0);
    fetchUsers();
  };
  const getColumns = (): ServerDataGridColumn[] => [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 50,
      maxWidth: 60,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "email",
      name: "Email Address",
      fieldName: "EmailAddress",
      minWidth: 250,
      maxWidth: 450,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "customers",
      name: "Customers",
      fieldName: "Customers",
      minWidth: 160,
      maxWidth: 260,
      isResizable: true,
      isPadded: true,
      onRender: (item: UserWithCustomers) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {(item.Customers || []).map((customer) => (
            <TooltipHost key={customer.Id} content={customer.Name}>
              <Persona
                hidePersonaDetails
                text={customer.Name}
                size={PersonaSize.size32}
              />
            </TooltipHost>
          ))}
        </div>
      ),
    },
    {
      key: "isActive",
      name: "Active",
      fieldName: "IsActive",
      minWidth: 90,
      maxWidth: 110,
      isResizable: true,
      isPadded: true,
      onRender: (item: UserWithCustomers) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            color: item.IsActive ? "#107C10" : "#A4262C",
            backgroundColor: item.IsActive ? "#DFF6DD" : "#FDE7E9",
            border: `1px solid ${item.IsActive ? "#A7D8A5" : "#F1AEB5"}`,
          }}
        >
          {item.IsActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: UserWithCustomers) => (
        item.Id === -1 ? null : (
          <MoreActionsButton
            menu={[
              {
                key: "view-access",
                text: "View Access",
                iconProps: { iconName: "Permissions" },
                onClick: () => {
                  setAccessUserId(item.Id);
                  setAccessUserEmail(item.EmailAddress);
                  setIsAccessPanelOpen(true);
                },
              },
              item.IsActive
                ? {
                    key: "deactivate",
                    text: "Deactivate user",
                    iconProps: { iconName: "Delete" },
                    onClick: () => {
                      setSelectedUser(item);
                    },
                  }
                : {
                    key: "reactivate",
                    text: "Reactivate user",
                    iconProps: { iconName: "Redo" },
                    onClick: async () => {
                      await reactivateUser(item.Id);
                      fetchUsers();
                    },
                  },
            ]}
          />
        )
      ),
    },
  ];

  return (
    <>
      <div
        className="page-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "white",
        }}
      >
        <h2 className="page-title">Users</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {allSelectedActive && (
            <IconButton
              iconProps={{ iconName: "Delete" }}
              title="Deactivate user"
              ariaLabel="Deactivate user"
              onClick={handleBulkDeactivate}
              disabled={loading}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: loading ? "#a19f9d" : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
          )}
          {allSelectedInactive && (
            <IconButton
              iconProps={{ iconName: "Redo" }}
              title="Reactivate user"
              ariaLabel="Reactivate user"
              onClick={handleBulkReactivate}
              disabled={loading}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: loading ? "#a19f9d" : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
          )}
          <SearchBox
            placeholder="Search"
            onSearch={handleSearch}
            disabled={loading}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              iconProps={{ iconName: "Filter" }}
              title="Filter users"
              ariaLabel="Filter users"
              onClick={openFilterPanel}
              disabled={loading}
              styles={{
                root: {
                  width: 40,
                  height: 40,
                  backgroundColor: hasStatusFilterApplied
                    ? "#deecf9"
                    : "transparent",
                },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: hasStatusFilterApplied
                    ? "#0078d4"
                    : loading
                    ? "#a19f9d"
                    : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
            {hasStatusFilterApplied && totalFiltersApplied > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  backgroundColor: "#d13438",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                {totalFiltersApplied}
              </div>
            )}
          </div>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={handleRefresh}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: {
                fontSize: 18,
                color: loading ? "#a19f9d" : "#0078d4",
              },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      <ServerDataGrid
        key={`users-grid-${currentPage}-${pageSize}`}
        items={users}
        columns={getColumns()}
        pageSize={pageSize}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={(page) => {
          setSelectedUserIds([]);
          setSelectedCount(0);
          setCurrentPage(page);
        }}
        onPageSizeChange={(size) => {
          setSelectedUserIds([]);
          setSelectedCount(0);
          setPageSize(size);
          setCurrentPage(1);
        }}
        loading={loading}
        selectedCount={selectedCount}
        onSelectedKeysChange={(keys) => {
          const normalizedKeys = keys
            .map((key) => Number(key))
            .filter((key) => !Number.isNaN(key));
          setSelectedUserIds(normalizedKeys);
          setSelectedCount(normalizedKeys.length);
        }}
      />

      <UsersFilterPanel
        isOpen={isFilterPanelOpen}
        onDismiss={closeFilterPanel}
        selectedStatus={selectedStatusFilter}
        onStatusChange={handleStatusFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
      <DeleteUser
        users={usersToDeactivate.length > 0 ? usersToDeactivate : selectedUser ? [selectedUser] : []}
        onClose={() => {
          setSelectedUser(undefined);
          setUsersToDeactivate([]);
        }}
        onDelete={() => {
          setSelectedUser(undefined);
          setUsersToDeactivate([]);
          fetchUsers();
        }}
      />
      <UserAccessPanel
        userId={accessUserId}
        userEmail={accessUserEmail}
        isOpen={isAccessPanelOpen}
        onDismiss={() => {
          setIsAccessPanelOpen(false);
          setAccessUserId(null);
          setAccessUserEmail("");
        }}
      />

      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSuccessMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          {successMessage}
        </MessageBar>
      )}

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default UsersPage;
